import { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import { cn } from '@/lib/utils';
import {
  characterHeight,
  drawCharacter,
  type AvatarConfig,
  type Facing,
} from '@/features/digitalOffice/avatars';
import {
  DEFAULT_LAYOUT,
  WORLD,
  findPath,
  isBlocked,
  moveWithCollision,
  pingPongSpotNear,
  setActiveLayout,
  zoneLabelAt,
  type OfficeLayout,
  type PingPongSpot,
} from '@/features/digitalOffice/map';
import type { OfficeLiveStore, PresenceStatus } from '@/features/digitalOffice/presence';
import {
  FLAT_KINDS,
  drawDoors,
  drawFlatFurniture,
  drawFloors,
  drawFurnitureLabels,
  drawFurniturePiece,
  drawPingPongBeacon,
  drawOutdoors,
  drawWalls,
  drawZoneLabels,
  inView,
  roundRectPath,
  type ViewRect,
} from '@/features/digitalOffice/render';
import {
  BENCH,
  benchRect,
  clampStationPosition,
  drawStationHandle,
  drawWorkbench,
  pointHitsStation,
  stationStandPoint,
  workerFacingAtStation,
  type FloorStation,
  type PinnedWorker,
} from '@/features/digitalOffice/floor';
import { visualBounds } from '@/features/digitalOffice/rotation';
import { formatClock } from '@/features/workMode/api';
import {
  addPlacedItem,
  applyRect,
  applyResizeRotated,
  applyRotation,
  applyTransform,
  canRotate,
  cursorForHandleRotated,
  dragRotation,
  getSelectedRect,
  getSelectedRotation,
  handleAtRotated,
  hitTestLayout,
  minSizeFor,
  placeSize,
  removeSelected,
  rotateHandleGap,
  type LayoutSelection,
  type PlaceKind,
  type ResizeHandle,
} from '@/features/digitalOffice/layout';

/**
 * Digital Office — the interactive canvas.
 *
 * One `requestAnimationFrame` loop owns everything hot: input, movement,
 * collision, remote interpolation, camera, and drawing. React props are
 * mirrored into refs so the loop never closes over stale values and the
 * component itself almost never re-renders.
 *
 * Controls: WASD / arrow keys walk (Shift to hurry), click walks there via
 * A*, drag pans, wheel zooms around the cursor.
 */

export interface OfflineCharacter {
  userId: string;
  name: string;
  config: AvatarConfig;
  x: number;
  y: number;
  facing: Facing;
}

export interface OfficeCanvasHandle {
  /** Pan the camera to a player (local, remote, offline, or pinned at a bench). */
  focusPlayer: (userId: string) => void;
  /** Pan the camera to a workstation. */
  focusStation: (stationId: string) => void;
  /** Zoom out to show the whole lot. */
  fitWorld: () => void;
  /** Re-centre on the local character and resume following. */
  followMe: () => void;
  /** Current local position (for persistence). */
  getPosition: () => { x: number; y: number; facing: Facing };
}

interface OfficeCanvasProps {
  me: { userId: string; name: string; config: AvatarConfig; status: PresenceStatus };
  initialPosition: { x: number; y: number; facing: Facing };
  store: OfficeLiveStore;
  offline: OfflineCharacter[];
  showOffline: boolean;
  /** False while a dialog is open so typing doesn't walk the character. */
  inputEnabled: boolean;
  /** `speed` is the current pace in world units / s so remotes can match it. */
  onMove: (x: number, y: number, facing: Facing, moving: boolean, speed: number) => void;
  /** Fires at most a few times a second while the local character moves. */
  onZoneChange?: (zone: string) => void;
  /** Workstations drawn as benches on the warehouse floor. */
  stations?: FloorStation[];
  /**
   * People signed in at a bench right now. They are drawn standing at the
   * station instead of wherever their free-roam character last was.
   */
  workers?: PinnedWorker[];
  /** Managers: drag benches to new spots. Disables click-to-walk on them. */
  arrangeMode?: boolean;
  onStationMoved?: (stationId: string, x: number, y: number) => void;
  /**
   * Privacy mode for screen recordings: no employee names above
   * characters. Bench pills keep their work line, roaming characters keep
   * only their emote bubble, and the local character is tagged "You".
   */
  hideNames?: boolean;
  /** Live building layout (walls, desks, rooms, pallets). Falls back to the default plan. */
  layout?: OfficeLayout;
  /** Super-admin: click / drag / resize every piece of the building. */
  editMode?: boolean;
  selection?: LayoutSelection | null;
  onSelect?: (sel: LayoutSelection | null) => void;
  onLayoutChange?: (layout: OfficeLayout) => void;
  placing?: PlaceKind | null;
  onPlacingChange?: (kind: PlaceKind | null) => void;
  onPlaceStation?: (x: number, y: number) => void;
  onStationResized?: (stationId: string, w: number, h: number, rotation?: number) => void;
  onStationRemoved?: (stationId: string) => void;
  /** Fires when the player walks up to or leaves a ping pong table. */
  onPingPongNear?: (spot: PingPongSpot | null) => void;
  className?: string;
}

const WALK_SPEED = 115; // world units / s
const RUN_SPEED = 185;
const MIN_ZOOM = 0.45;
const MAX_ZOOM = 3.2;
const DEFAULT_ZOOM = 1.9;

const STATUS_COLORS: Record<PresenceStatus | 'offline', string> = {
  online: '#4ADE80',
  away: '#F5B547',
  offline: '#9CA3AF',
};

const MOVE_KEYS: Record<string, [number, number]> = {
  w: [0, -1],
  a: [-1, 0],
  s: [0, 1],
  d: [1, 0],
  arrowup: [0, -1],
  arrowleft: [-1, 0],
  arrowdown: [0, 1],
  arrowright: [1, 0],
};

interface LocalPlayer {
  x: number;
  y: number;
  facing: Facing;
  walked: number;
  moving: boolean;
  path: Array<{ x: number; y: number }> | null;
  stuckFor: number;
}

interface Camera {
  x: number;
  y: number;
  zoom: number;
  /** Where the camera wants to be (for smooth pans). */
  tx: number;
  ty: number;
  follow: boolean;
}

const NO_STATIONS: FloorStation[] = [];
const NO_WORKERS: PinnedWorker[] = [];

export const OfficeCanvas = forwardRef<OfficeCanvasHandle, OfficeCanvasProps>(function OfficeCanvas(
  {
    me,
    initialPosition,
    store,
    offline,
    showOffline,
    inputEnabled,
    onMove,
    onZoneChange,
    stations = NO_STATIONS,
    workers = NO_WORKERS,
    arrangeMode = false,
    onStationMoved,
    hideNames = false,
    layout = DEFAULT_LAYOUT,
    editMode = false,
    selection = null,
    onSelect,
    onLayoutChange,
    placing = null,
    onPlacingChange,
    onPlaceStation,
    onStationResized,
    onStationRemoved,
    onPingPongNear,
    className,
  },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Mirrors of props for the loop.
  const meRef = useRef(me);
  meRef.current = me;
  const offlineRef = useRef(offline);
  offlineRef.current = offline;
  const showOfflineRef = useRef(showOffline);
  showOfflineRef.current = showOffline;
  const inputEnabledRef = useRef(inputEnabled);
  inputEnabledRef.current = inputEnabled;
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;
  const onZoneChangeRef = useRef(onZoneChange);
  onZoneChangeRef.current = onZoneChange;
  const stationsRef = useRef(stations);
  stationsRef.current = stations;
  const workersRef = useRef(workers);
  workersRef.current = workers;
  const arrangeRef = useRef(arrangeMode);
  arrangeRef.current = arrangeMode;
  const onStationMovedRef = useRef(onStationMoved);
  onStationMovedRef.current = onStationMoved;
  const hideNamesRef = useRef(hideNames);
  hideNamesRef.current = hideNames;
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const editRef = useRef(editMode);
  editRef.current = editMode;
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const onLayoutChangeRef = useRef(onLayoutChange);
  onLayoutChangeRef.current = onLayoutChange;
  const placingRef = useRef(placing);
  placingRef.current = placing;
  const onPlacingChangeRef = useRef(onPlacingChange);
  onPlacingChangeRef.current = onPlacingChange;
  const onPlaceStationRef = useRef(onPlaceStation);
  onPlaceStationRef.current = onPlaceStation;
  const onStationResizedRef = useRef(onStationResized);
  onStationResizedRef.current = onStationResized;
  const onStationRemovedRef = useRef(onStationRemoved);
  onStationRemovedRef.current = onStationRemoved;
  const onPingPongRef = useRef(onPingPongNear);
  onPingPongRef.current = onPingPongNear;

  useEffect(() => {
    setActiveLayout(layout);
  }, [layout]);

  // A bench being dragged in arrange mode. After the drop it lingers until
  // the parent re-renders with the new position (optimistically or from
  // the server) so the bench never snaps back for a frame.
  const stationDragRef = useRef<{
    id: string;
    dx: number;
    dy: number;
    x: number;
    y: number;
    moved: boolean;
    dropped: boolean;
  } | null>(null);
  useEffect(() => {
    if (stationDragRef.current?.dropped) stationDragRef.current = null;
  }, [stations]);
  useEffect(() => {
    if (!arrangeMode && !editMode) stationDragRef.current = null;
  }, [arrangeMode, editMode]);

  const editDragRef = useRef<{
    sel: LayoutSelection;
    mode: 'move' | 'resize' | 'rotate';
    handle?: ResizeHandle;
    start: { x: number; y: number; w: number; h: number };
    origin: { x: number; y: number };
    current: { x: number; y: number; w: number; h: number };
    startRotation: number;
    rotation: number;
    moved: boolean;
  } | null>(null);
  useEffect(() => {
    if (!editMode) editDragRef.current = null;
  }, [editMode]);

  const playerRef = useRef<LocalPlayer>({
    x: initialPosition.x,
    y: initialPosition.y,
    facing: initialPosition.facing,
    walked: 0,
    moving: false,
    path: null,
    stuckFor: 0,
  });
  const camRef = useRef<Camera>({
    x: initialPosition.x,
    y: initialPosition.y,
    zoom: DEFAULT_ZOOM,
    tx: initialPosition.x,
    ty: initialPosition.y,
    follow: true,
  });
  const keysRef = useRef<Set<string>>(new Set());
  const shiftRef = useRef(false);
  const sizeRef = useRef({ w: 1, h: 1, dpr: 1 });
  const dragRef = useRef<{
    active: boolean;
    moved: boolean;
    startX: number;
    startY: number;
    lastX: number;
    lastY: number;
    pointerId: number | null;
  }>({ active: false, moved: false, startX: 0, startY: 0, lastX: 0, lastY: 0, pointerId: null });
  const markerRef = useRef<{ x: number; y: number; until: number } | null>(null);
  const hoverRef = useRef<{ x: number; y: number } | null>(null);
  const lastZoneRef = useRef<string>('');

  // ── Imperative API ──────────────────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    focusPlayer: (userId) => {
      const cam = camRef.current;
      const pinned = workersRef.current.find((w) => w.userId === userId);
      if (!pinned && userId === meRef.current.userId) {
        cam.follow = true;
        return;
      }
      const remote = store.players.get(userId);
      const target =
        pinned ??
        (remote
          ? { x: remote.x, y: remote.y }
          : (offlineRef.current.find((o) => o.userId === userId) ?? null));
      if (!target) return;
      cam.follow = false;
      cam.tx = target.x;
      cam.ty = target.y;
      if (cam.zoom < 1.4) cam.zoom = 1.9;
    },
    focusStation: (stationId) => {
      const s = stationsRef.current.find((x) => x.id === stationId);
      if (!s) return;
      const cam = camRef.current;
      cam.follow = false;
      cam.tx = s.x;
      cam.ty = s.y - 10;
      if (cam.zoom < 1.4) cam.zoom = 1.9;
    },
    fitWorld: () => {
      const cam = camRef.current;
      const { w, h } = sizeRef.current;
      cam.zoom = Math.max(MIN_ZOOM, Math.min(w / WORLD.w, h / WORLD.h) * 0.96);
      cam.follow = false;
      cam.tx = WORLD.w / 2;
      cam.ty = WORLD.h / 2;
    },
    followMe: () => {
      const cam = camRef.current;
      cam.follow = true;
      if (cam.zoom < 1.2) cam.zoom = DEFAULT_ZOOM;
    },
    getPosition: () => {
      const p = playerRef.current;
      return { x: p.x, y: p.y, facing: p.facing };
    },
  }));

  // ── Main loop ───────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Resize handling
    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const w = Math.max(1, Math.floor(rect.width));
      const h = Math.max(1, Math.floor(rect.height));
      sizeRef.current = { w, h, dpr };
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);

    // ── Input ─────────────────────────────────────────────────────────────
    const isTypingTarget = (el: EventTarget | null) => {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName.toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') shiftRef.current = true;
      if (isTypingTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (editRef.current) {
        if (k === 'escape') {
          e.preventDefault();
          editDragRef.current = null;
          onPlacingChangeRef.current?.(null);
          onSelectRef.current?.(null);
          return;
        }
        if (k === 'delete' || k === 'backspace') {
          const sel = selectionRef.current;
          if (!sel) return;
          e.preventDefault();
          if (sel.layer === 'station') onStationRemovedRef.current?.(sel.id);
          else onLayoutChangeRef.current?.(removeSelected(layoutRef.current, sel));
          onSelectRef.current?.(null);
          return;
        }
        if (k === 'r') {
          if (e.repeat) return;
          const sel = selectionRef.current;
          if (!sel || !canRotate(sel.layer)) return;
          e.preventDefault();
          const delta = e.shiftKey ? -90 : 90;
          if (sel.layer === 'station') {
            const s = stationsRef.current.find((x) => x.id === sel.id);
            if (!s) return;
            const rot = ((s.rotation ?? 0) + delta + 360) % 360;
            onStationResizedRef.current?.(s.id, s.w ?? BENCH.w, s.h ?? BENCH.h, rot);
            return;
          }
          const cur = getSelectedRotation(layoutRef.current, sel);
          onLayoutChangeRef.current?.(applyRotation(layoutRef.current, sel, cur + delta));
          return;
        }
        if (['arrowup', 'arrowdown', 'arrowleft', 'arrowright'].includes(k)) {
          const sel = selectionRef.current;
          if (!sel) return;
          e.preventDefault();
          const step = e.shiftKey ? 8 : 1;
          const dx = k === 'arrowleft' ? -step : k === 'arrowright' ? step : 0;
          const dy = k === 'arrowup' ? -step : k === 'arrowdown' ? step : 0;
          if (sel.layer === 'station') {
            const s = stationsRef.current.find((x) => x.id === sel.id);
            if (s) onStationMovedRef.current?.(s.id, s.x + dx, s.y + dy);
            return;
          }
          const r = getSelectedRect(layoutRef.current, sel);
          if (!r) return;
          onLayoutChangeRef.current?.(
            applyRect(layoutRef.current, sel, { ...r, x: r.x + dx, y: r.y + dy }),
          );
          return;
        }
        return;
      }
      if (!inputEnabledRef.current) return;
      if (k === 'shift') shiftRef.current = true;
      if (MOVE_KEYS[k]) {
        e.preventDefault();
        keysRef.current.add(k);
        playerRef.current.path = null;
        camRef.current.follow = true;
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      if (k === 'shift') shiftRef.current = false;
      keysRef.current.delete(k);
    };
    const onBlur = () => {
      keysRef.current.clear();
      shiftRef.current = false;
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);

    const screenToWorld = (sx: number, sy: number) => {
      const cam = camRef.current;
      const { w, h } = sizeRef.current;
      return {
        x: (sx - w / 2) / cam.zoom + cam.x,
        y: (sy - h / 2) / cam.zoom + cam.y,
      };
    };

    const stationPosition = (s: FloorStation) => {
      const d = stationDragRef.current;
      return d && d.id === s.id ? { x: d.x, y: d.y } : { x: s.x, y: s.y };
    };
    const stationAt = (wx: number, wy: number): FloorStation | null => {
      const list = stationsRef.current;
      for (let i = list.length - 1; i >= 0; i--) {
        const s = list[i];
        const p = stationPosition(s);
        if (pointHitsStation(s, p.x, p.y, wx, wy)) return s;
      }
      return null;
    };

    const stationBenchRect = (s: FloorStation) => {
      const p = stationPosition(s);
      return benchRect(p.x, p.y, s.w, s.h);
    };

    const liveStation = (s: FloorStation, pos = stationPosition(s)): FloorStation => {
      const drag = editDragRef.current;
      if (drag && drag.sel.layer === 'station' && drag.sel.id === s.id) {
        if (drag.mode === 'resize') {
          return {
            ...s,
            x: drag.current.x + drag.current.w / 2,
            y: drag.current.y + drag.current.h + BENCH.gap,
            w: drag.current.w,
            h: drag.current.h,
            rotation: drag.rotation,
          };
        }
        return { ...s, x: pos.x, y: pos.y, rotation: drag.rotation };
      }
      return { ...s, x: pos.x, y: pos.y };
    };

    const selectedRect = () => {
      const sel = selectionRef.current;
      if (!sel) return null;
      const drag = editDragRef.current;
      if (drag && drag.sel.layer === sel.layer && drag.sel.id === sel.id) return drag.current;
      if (sel.layer === 'station') {
        const s = stationsRef.current.find((x) => x.id === sel.id);
        return s ? stationBenchRect(s) : null;
      }
      return getSelectedRect(visualLayout(), sel);
    };

    const visualLayout = (): OfficeLayout => {
      const base = layoutRef.current;
      const d = editDragRef.current;
      if (!d || d.sel.layer === 'station') return base;
      return applyTransform(base, d.sel, d.current, d.rotation);
    };

    const selectedRotation = () => {
      const sel = selectionRef.current;
      if (!sel) return 0;
      const drag = editDragRef.current;
      if (drag && drag.sel.layer === sel.layer && drag.sel.id === sel.id) return drag.rotation;
      if (sel.layer === 'station') {
        const s = stationsRef.current.find((x) => x.id === sel.id);
        return s?.rotation ?? 0;
      }
      return getSelectedRotation(layoutRef.current, sel);
    };

    const handleSizeWorld = (zoom: number) => Math.max(4, 8 / zoom);

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      canvas.setPointerCapture(e.pointerId);
      const rect = canvas.getBoundingClientRect();
      const wp = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
      canvas.focus();

      if (editRef.current) {
        const place = placingRef.current;
        if (place) {
          if (place === 'station') {
            onPlaceStationRef.current?.(wp.x, wp.y);
          } else {
            const added = addPlacedItem(layoutRef.current, place, wp.x, wp.y);
            onLayoutChangeRef.current?.(added.layout);
            onSelectRef.current?.(added.selection);
          }
          onPlacingChangeRef.current?.(null);
          dragRef.current.active = false;
          return;
        }
        const zoom = camRef.current.zoom;
        const sel = selectionRef.current;
        const hs = handleSizeWorld(zoom);
        if (sel) {
          const r = selectedRect();
          if (r) {
            const rot = selectedRotation();
            const handle = handleAtRotated(
              r,
              rot,
              wp.x,
              wp.y,
              hs,
              rotateHandleGap(zoom),
              canRotate(sel.layer),
            );
            if (handle === 'rotate') {
              editDragRef.current = {
                sel,
                mode: 'rotate',
                start: { ...r },
                origin: { x: wp.x, y: wp.y },
                current: { ...r },
                startRotation: rot,
                rotation: rot,
                moved: false,
              };
              dragRef.current.active = false;
              canvas.style.cursor = 'grabbing';
              return;
            }
            if (handle) {
              editDragRef.current = {
                sel,
                mode: 'resize',
                handle,
                start: { ...r },
                origin: { x: wp.x, y: wp.y },
                current: { ...r },
                startRotation: rot,
                rotation: rot,
                moved: false,
              };
              dragRef.current.active = false;
              canvas.style.cursor = cursorForHandleRotated(handle, rot, false);
              return;
            }
          }
        }
        const stationHit = stationAt(wp.x, wp.y);
        if (stationHit) {
          onSelectRef.current?.({ layer: 'station', id: stationHit.id });
          const p = stationPosition(stationHit);
          stationDragRef.current = {
            id: stationHit.id,
            dx: wp.x - p.x,
            dy: wp.y - p.y,
            x: p.x,
            y: p.y,
            moved: false,
            dropped: false,
          };
          dragRef.current.active = false;
          canvas.style.cursor = 'grabbing';
          return;
        }
        const hit = hitTestLayout(layoutRef.current, wp.x, wp.y);
        if (hit) {
          onSelectRef.current?.(hit);
          const r = getSelectedRect(layoutRef.current, hit);
          if (r) {
            const rot = getSelectedRotation(layoutRef.current, hit);
            editDragRef.current = {
              sel: hit,
              mode: 'move',
              start: { ...r },
              origin: { x: wp.x, y: wp.y },
              current: { ...r },
              startRotation: rot,
              rotation: rot,
              moved: false,
            };
            canvas.style.cursor = 'grabbing';
          }
          dragRef.current.active = false;
          return;
        }
        onSelectRef.current?.(null);
      }

      if (
        (arrangeRef.current || editRef.current) &&
        onStationMovedRef.current &&
        !editRef.current
      ) {
        const hit = stationAt(wp.x, wp.y);
        if (hit) {
          const p = stationPosition(hit);
          stationDragRef.current = {
            id: hit.id,
            dx: wp.x - p.x,
            dy: wp.y - p.y,
            x: p.x,
            y: p.y,
            moved: false,
            dropped: false,
          };
          dragRef.current.active = false;
          canvas.style.cursor = 'grabbing';
          return;
        }
      }
      dragRef.current = {
        active: true,
        moved: false,
        startX: e.clientX - rect.left,
        startY: e.clientY - rect.top,
        lastX: e.clientX - rect.left,
        lastY: e.clientY - rect.top,
        pointerId: e.pointerId,
      };
    };
    const onPointerMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      hoverRef.current = screenToWorld(sx, sy);
      const ed = editDragRef.current;
      if (ed) {
        const wp = hoverRef.current;
        const dx = wp.x - ed.origin.x;
        const dy = wp.y - ed.origin.y;
        if (ed.mode === 'rotate') {
          ed.rotation = dragRotation(ed.start, ed.startRotation, ed.origin, wp, shiftRef.current);
          ed.moved = true;
          canvas.style.cursor = 'grabbing';
          return;
        }
        if (ed.mode === 'resize' && ed.handle) {
          const min = minSizeFor(ed.sel.layer);
          ed.current = applyResizeRotated(
            ed.start,
            ed.handle,
            dx,
            dy,
            ed.startRotation,
            min.w,
            min.h,
          );
        } else {
          ed.current = { ...ed.start, x: ed.start.x + dx, y: ed.start.y + dy };
        }
        ed.moved = true;
        return;
      }
      const sd = stationDragRef.current;
      if (sd && !sd.dropped) {
        const wp = hoverRef.current;
        sd.x = wp.x - sd.dx;
        sd.y = wp.y - sd.dy;
        sd.moved = true;
        return;
      }
      if (editRef.current) {
        const wp = hoverRef.current;
        if (placingRef.current) {
          canvas.style.cursor = 'copy';
        } else {
          const r = selectedRect();
          const zoom = camRef.current.zoom;
          const sel = selectionRef.current;
          const handle = r
            ? handleAtRotated(
                r,
                selectedRotation(),
                wp.x,
                wp.y,
                handleSizeWorld(zoom),
                rotateHandleGap(zoom),
                !!sel && canRotate(sel.layer),
              )
            : null;
          if (handle)
            canvas.style.cursor = cursorForHandleRotated(handle, selectedRotation(), false);
          else if (stationAt(wp.x, wp.y) || hitTestLayout(layoutRef.current, wp.x, wp.y))
            canvas.style.cursor = 'move';
          else canvas.style.cursor = '';
        }
      } else if (arrangeRef.current) {
        canvas.style.cursor = stationAt(hoverRef.current.x, hoverRef.current.y) ? 'grab' : '';
      } else if (canvas.style.cursor) {
        canvas.style.cursor = '';
      }
      const d = dragRef.current;
      if (!d.active) return;
      const dx = sx - d.lastX;
      const dy = sy - d.lastY;
      if (!d.moved && Math.hypot(sx - d.startX, sy - d.startY) > 4) d.moved = true;
      if (d.moved) {
        const cam = camRef.current;
        cam.follow = false;
        cam.x -= dx / cam.zoom;
        cam.y -= dy / cam.zoom;
        cam.tx = cam.x;
        cam.ty = cam.y;
      }
      d.lastX = sx;
      d.lastY = sy;
    };
    const onPointerUp = (e: PointerEvent) => {
      const ed = editDragRef.current;
      if (ed) {
        try {
          canvas.releasePointerCapture(e.pointerId);
        } catch {
          /* already released */
        }
        if (ed.moved) {
          if (ed.sel.layer === 'station') {
            const b = ed.current;
            const standX = Math.round(b.x + b.w / 2);
            const standY = Math.round(b.y + b.h + BENCH.gap);
            if (ed.mode !== 'rotate') onStationMovedRef.current?.(ed.sel.id, standX, standY);
            onStationResizedRef.current?.(ed.sel.id, Math.round(b.w), Math.round(b.h), ed.rotation);
          } else {
            onLayoutChangeRef.current?.(
              applyTransform(layoutRef.current, ed.sel, ed.current, ed.rotation),
            );
          }
        }
        editDragRef.current = null;
        canvas.style.cursor = 'move';
        return;
      }
      const sd = stationDragRef.current;
      if (sd && !sd.dropped) {
        try {
          canvas.releasePointerCapture(e.pointerId);
        } catch {
          /* already released */
        }
        canvas.style.cursor = 'grab';
        if (!sd.moved) {
          stationDragRef.current = null;
          return;
        }
        const st = stationsRef.current.find((s) => s.id === sd.id);
        const snapped = clampStationPosition(sd.x, sd.y, {
          free: editRef.current,
          w: st?.w,
          h: st?.h,
        });
        sd.x = snapped.x;
        sd.y = snapped.y;
        sd.dropped = true;
        onStationMovedRef.current?.(sd.id, snapped.x, snapped.y);
        return;
      }
      const d = dragRef.current;
      if (!d.active) return;
      d.active = false;
      if (d.pointerId !== null) {
        try {
          canvas.releasePointerCapture(d.pointerId);
        } catch {
          /* already released */
        }
      }
      if (d.moved || !inputEnabledRef.current || editRef.current || arrangeRef.current) return;
      const rect = canvas.getBoundingClientRect();
      const target = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
      const p = playerRef.current;
      const path = findPath(p.x, p.y, target.x, target.y);
      if (path && path.length) {
        p.path = path;
        const last = path[path.length - 1];
        markerRef.current = { x: last.x, y: last.y, until: performance.now() + 1200 };
        camRef.current.follow = true;
      }
    };
    const onPointerLeave = () => {
      hoverRef.current = null;
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const cam = camRef.current;
      const rect = canvas.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      const before = screenToWorld(sx, sy);
      const factor = Math.exp(-e.deltaY * 0.0014);
      cam.zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, cam.zoom * factor));
      const after = screenToWorld(sx, sy);
      // Keep the world point under the cursor fixed.
      cam.x += before.x - after.x;
      cam.y += before.y - after.y;
      if (!cam.follow) {
        cam.tx = cam.x;
        cam.ty = cam.y;
      }
    };
    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointercancel', onPointerUp);
    canvas.addEventListener('pointerleave', onPointerLeave);
    canvas.addEventListener('wheel', onWheel, { passive: false });

    // ── Frame ─────────────────────────────────────────────────────────────
    let raf = 0;
    let last = performance.now();
    let wasMoving = false;
    let zoneTimer = 0;
    let nearTimer = 0;
    let lastNearId: string | null = null;
    const nearSpotRef: { current: PingPongSpot | null } = { current: null };

    const frame = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;

      const p = playerRef.current;
      const cam = camRef.current;
      const { w, h, dpr } = sizeRef.current;

      // ── Local movement ──
      let vx = 0;
      let vy = 0;
      if (inputEnabledRef.current) {
        for (const k of keysRef.current) {
          const v = MOVE_KEYS[k];
          if (v) {
            vx += v[0];
            vy += v[1];
          }
        }
      }
      const speed = shiftRef.current ? RUN_SPEED : WALK_SPEED;
      let moved = false;
      if (vx !== 0 || vy !== 0) {
        const len = Math.hypot(vx, vy);
        const dx = (vx / len) * speed * dt;
        const dy = (vy / len) * speed * dt;
        const next = moveWithCollision(p.x, p.y, dx, dy);
        moved = next.x !== p.x || next.y !== p.y;
        if (moved) {
          p.walked += Math.hypot(next.x - p.x, next.y - p.y);
          p.x = next.x;
          p.y = next.y;
        }
        p.facing =
          Math.abs(vx) > Math.abs(vy) ? (vx > 0 ? 'right' : 'left') : vy > 0 ? 'down' : 'up';
        p.path = null;
      } else if (p.path && p.path.length) {
        const target = p.path[0];
        const dx = target.x - p.x;
        const dy = target.y - p.y;
        const dist = Math.hypot(dx, dy);
        if (dist < 1.5) {
          p.path.shift();
          if (!p.path.length) p.path = null;
        } else {
          const step = Math.min(dist, speed * dt);
          const sx = (dx / dist) * step;
          const sy = (dy / dist) * step;
          const next = moveWithCollision(p.x, p.y, sx, sy);
          moved = next.x !== p.x || next.y !== p.y;
          if (moved) {
            p.walked += Math.hypot(next.x - p.x, next.y - p.y);
            p.x = next.x;
            p.y = next.y;
            p.stuckFor = 0;
          } else {
            p.stuckFor += dt;
            if (p.stuckFor > 0.4) {
              p.path = null;
              p.stuckFor = 0;
            }
          }
          p.facing =
            Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : dy > 0 ? 'down' : 'up';
        }
      }
      p.moving = moved;
      if (moved) {
        onMoveRef.current(p.x, p.y, p.facing, true, speed);
      } else if (wasMoving) {
        onMoveRef.current(p.x, p.y, p.facing, false, 0);
      }
      wasMoving = moved;
      store.me = { x: p.x, y: p.y, facing: p.facing };

      nearTimer += dt;
      if (nearTimer > 0.12) {
        nearTimer = 0;
        const spot =
          editRef.current || arrangeRef.current
            ? null
            : pingPongSpotNear(layoutRef.current.furniture, p.x, p.y);
        nearSpotRef.current = spot;
        const id = spot?.id ?? null;
        if (id !== lastNearId) {
          lastNearId = id;
          onPingPongRef.current?.(spot);
        }
      }

      zoneTimer += dt;
      if (zoneTimer > 0.4) {
        zoneTimer = 0;
        const z = zoneLabelAt(p.x, p.y);
        if (z !== lastZoneRef.current) {
          lastZoneRef.current = z;
          onZoneChangeRef.current?.(z);
        }
      }

      // ── Remote interpolation ──
      store.tick(dt);

      // ── Camera ──
      if (cam.follow) {
        cam.tx = p.x;
        cam.ty = p.y - 6;
      }
      const k = Math.min(1, dt * 7);
      cam.x += (cam.tx - cam.x) * k;
      cam.y += (cam.ty - cam.y) * k;
      // Clamp so we never show much beyond the lot.
      const halfW = w / 2 / cam.zoom;
      const halfH = h / 2 / cam.zoom;
      const margin = 24;
      if (halfW * 2 >= WORLD.w + margin * 2) cam.x = WORLD.w / 2;
      else cam.x = Math.max(halfW - margin, Math.min(WORLD.w - halfW + margin, cam.x));
      if (halfH * 2 >= WORLD.h + margin * 2) cam.y = WORLD.h / 2;
      else cam.y = Math.max(halfH - margin, Math.min(WORLD.h - halfH + margin, cam.y));

      const view: ViewRect = { x: cam.x - halfW, y: cam.y - halfH, w: halfW * 2, h: halfH * 2 };
      const scene = visualLayout();

      // ── Draw ──
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = '#B9C9A2';
      ctx.fillRect(0, 0, w, h);
      ctx.setTransform(
        dpr * cam.zoom,
        0,
        0,
        dpr * cam.zoom,
        dpr * (w / 2 - cam.x * cam.zoom),
        dpr * (h / 2 - cam.y * cam.zoom),
      );

      drawOutdoors(ctx, view);
      drawFloors(ctx, view, scene.floors);
      if (editRef.current) {
        ctx.save();
        for (const z of scene.zones) {
          if (!inView(z, view)) continue;
          const sel = selectionRef.current;
          const on = sel?.layer === 'zone' && sel.id === z.id;
          ctx.setLineDash(on ? [] : [5, 4]);
          ctx.strokeStyle = on ? 'rgba(27,46,29,0.55)' : 'rgba(27,46,29,0.16)';
          ctx.lineWidth = on ? 1.1 : 0.7;
          ctx.strokeRect(z.x, z.y, z.w, z.h);
        }
        ctx.restore();
      }
      drawZoneLabels(ctx, view, cam.zoom, scene.zones);
      drawDoors(ctx, view, scene.doors);
      drawWalls(ctx, view, scene.walls);
      drawFlatFurniture(ctx, view, scene.furniture);

      // Click-to-move marker
      const marker = markerRef.current;
      if (marker) {
        const t = (marker.until - now) / 1200;
        if (t <= 0) markerRef.current = null;
        else {
          ctx.strokeStyle = `rgba(27,46,29,${0.5 * t})`;
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.ellipse(marker.x, marker.y, 6 + (1 - t) * 6, 3 + (1 - t) * 3, 0, 0, Math.PI * 2);
          ctx.stroke();
        }
      }

      // Hover cursor hint (walkable or not)
      const hover = hoverRef.current;
      if (hover && !dragRef.current.moved && inputEnabledRef.current && !editRef.current) {
        const blocked = isBlocked(hover.x, hover.y);
        ctx.strokeStyle = blocked ? 'rgba(142,68,50,0.45)' : 'rgba(27,46,29,0.28)';
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.ellipse(hover.x, hover.y, 5, 2.5, 0, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Path preview
      if (p.path && p.path.length) {
        ctx.strokeStyle = 'rgba(27,46,29,0.22)';
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        for (const wp of p.path) ctx.lineTo(wp.x, wp.y);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // ── Y-sorted entities (furniture + characters) ──
      type Entity =
        | { kind: 'furniture'; y: number; idx: number }
        | { kind: 'remote'; y: number; id: string }
        | { kind: 'offline'; y: number; idx: number }
        | { kind: 'station'; y: number; idx: number }
        | { kind: 'worker'; y: number; idx: number }
        | { kind: 'me'; y: number };
      const entities: Entity[] = [];
      scene.furniture.forEach((f, idx) => {
        const bounds = visualBounds(f);
        if (!FLAT_KINDS.has(f.kind) && inView(bounds, view)) {
          entities.push({ kind: 'furniture', y: bounds.y + bounds.h, idx });
        }
      });

      // People signed in at a bench are drawn there, not where their
      // free-roam character last stood.
      const stationList = stationsRef.current;
      const workerList = workersRef.current;
      const pinnedIds = new Set(workerList.map((w) => w.userId));
      const workerByStation = new Map(workerList.map((w) => [w.stationId, w]));
      const stationPos = new Map<string, { x: number; y: number }>();
      stationList.forEach((s, idx) => {
        const pos = stationPosition(s);
        stationPos.set(s.id, pos);
        const live = liveStation(s, pos);
        const vis = stationStandPoint(live);
        if (inView({ x: vis.x - 24, y: vis.y - 36, w: 48, h: 48 }, view)) {
          entities.push({ kind: 'station', y: vis.y - BENCH.gap, idx });
        }
      });
      workerList.forEach((wk, idx) => {
        const st = stationList.find((s) => s.id === wk.stationId);
        const pos = stationPos.get(wk.stationId) ?? { x: wk.x, y: wk.y };
        const vis = st ? stationStandPoint(liveStation(st, pos)) : pos;
        if (inView({ x: vis.x - 12, y: vis.y - 34, w: 24, h: 40 }, view)) {
          entities.push({ kind: 'worker', y: vis.y, idx });
        }
      });

      for (const rp of store.players.values()) {
        if (pinnedIds.has(rp.userId)) continue;
        if (inView({ x: rp.x - 12, y: rp.y - 34, w: 24, h: 40 }, view)) {
          entities.push({ kind: 'remote', y: rp.y, id: rp.userId });
        }
      }
      if (showOfflineRef.current) {
        offlineRef.current.forEach((o, idx) => {
          if (store.players.has(o.userId) || pinnedIds.has(o.userId)) return;
          if (inView({ x: o.x - 12, y: o.y - 34, w: 24, h: 40 }, view)) {
            entities.push({ kind: 'offline', y: o.y, idx });
          }
        });
      }
      if (!pinnedIds.has(meRef.current.userId)) entities.push({ kind: 'me', y: p.y });
      entities.sort((a, b) => a.y - b.y);

      const labels: Array<{
        x: number;
        y: number;
        name: string;
        status: PresenceStatus | 'offline';
        isMe: boolean;
        emote: string | null;
        alpha: number;
        /** Second line on the pill (pinned workers: item · count · timer). */
        sub?: string;
        tone?: 'working' | 'break';
      }> = [];
      const stationTags: Array<{
        x: number;
        y: number;
        name: string;
        placed: boolean;
        occupied: boolean;
      }> = [];

      for (const e of entities) {
        if (e.kind === 'furniture') {
          drawFurniturePiece(ctx, scene.furniture[e.idx], e.idx);
        } else if (e.kind === 'station') {
          const s = stationList[e.idx];
          if (!s) continue;
          const pos = stationPos.get(s.id) ?? s;
          const wk = workerByStation.get(s.id);
          const sized = liveStation(s, pos);
          const vis = stationStandPoint(sized);
          const dragging = stationDragRef.current?.id === s.id;
          if (arrangeRef.current || editRef.current) drawStationHandle(ctx, sized, dragging);
          drawWorkbench(ctx, sized, wk ? (wk.onBreak ? 'break' : 'working') : 'empty', dragging);
          stationTags.push({ x: vis.x, y: vis.y, name: s.name, placed: s.placed, occupied: !!wk });
        } else if (e.kind === 'worker') {
          const wk = workerList[e.idx];
          if (!wk) continue;
          const st = stationList.find((s) => s.id === wk.stationId);
          const stored = stationPos.get(wk.stationId) ?? { x: wk.x, y: wk.y };
          const live = st ? liveStation(st, stored) : null;
          const pos = live ? stationStandPoint(live) : stored;
          drawCharacter(ctx, wk.config, {
            x: pos.x,
            y: pos.y,
            facing: wk.onBreak ? 'down' : workerFacingAtStation(live?.rotation),
            alpha: wk.onBreak ? 0.8 : 1,
          });
          const elapsed = Math.max(0, (Date.now() - wk.unitStartedAt) / 1000);
          const what = wk.itemName ?? wk.stationName;
          const sub = wk.onBreak
            ? `${wk.breakLabel ?? 'On a break'} · ${wk.unitsDone} done`
            : `${what} · #${wk.unitsDone + 1} · ${formatClock(elapsed)}`;
          labels.push({
            x: pos.x,
            y: pos.y,
            name: wk.name,
            status: wk.onBreak ? 'away' : 'online',
            isMe: wk.userId === meRef.current.userId,
            emote: store.players.get(wk.userId)?.emote?.text ?? null,
            alpha: 1,
            sub,
            tone: wk.onBreak ? 'break' : 'working',
          });
        } else if (e.kind === 'remote') {
          const rp = store.players.get(e.id);
          if (!rp) continue;
          const walking = Math.hypot(rp.tx - rp.x, rp.ty - rp.y) > 0.6;
          const status = store.displayStatus(rp, now);
          drawCharacter(ctx, rp.config, {
            x: rp.x,
            y: rp.y,
            facing: rp.facing,
            walkPhase: walking ? (rp.walked / 22) % 1 : undefined,
            alpha: status === 'away' ? 0.75 : 1,
          });
          labels.push({
            x: rp.x,
            y: rp.y,
            name: rp.name,
            status,
            isMe: false,
            emote: rp.emote?.text ?? null,
            alpha: 1,
          });
        } else if (e.kind === 'offline') {
          const o = offlineRef.current[e.idx];
          if (!o) continue;
          drawCharacter(ctx, o.config, { x: o.x, y: o.y, facing: o.facing, alpha: 0.32 });
          labels.push({
            x: o.x,
            y: o.y,
            name: o.name,
            status: 'offline',
            isMe: false,
            emote: null,
            alpha: 0.55,
          });
        } else {
          drawCharacter(ctx, meRef.current.config, {
            x: p.x,
            y: p.y,
            facing: p.facing,
            walkPhase: p.moving ? (p.walked / 22) % 1 : undefined,
          });
          labels.push({
            x: p.x,
            y: p.y,
            name: meRef.current.name,
            status: meRef.current.status,
            isMe: true,
            emote: store.myEmote?.text ?? null,
            alpha: 1,
          });
        }
      }

      drawFurnitureLabels(ctx, view, cam.zoom, scene.furniture);
      if (nearSpotRef.current && !editRef.current && !arrangeRef.current) {
        drawPingPongBeacon(ctx, nearSpotRef.current, now);
      }

      if (editRef.current) {
        const place = placingRef.current;
        if (place && hover) {
          const size = placeSize(place);
          ctx.save();
          ctx.globalAlpha = 0.45;
          ctx.fillStyle = place === 'wall' ? '#F7F5EF' : place === 'rack' ? '#4E545A' : '#6B7177';
          ctx.strokeStyle = 'rgba(27,46,29,0.7)';
          ctx.lineWidth = 0.9;
          ctx.setLineDash([3, 2]);
          const gx = hover.x - size.w / 2;
          const gy = hover.y - size.h / 2;
          if (place === 'circle') {
            ctx.beginPath();
            ctx.ellipse(hover.x, hover.y, size.w / 2, size.h / 2, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
          } else {
            ctx.fillRect(gx, gy, size.w, size.h);
            ctx.strokeRect(gx, gy, size.w, size.h);
          }
          ctx.restore();
        }
        const r = selectedRect();
        if (r) {
          const zoom = cam.zoom;
          const rot = selectedRotation();
          const cx = r.x + r.w / 2;
          const cy = r.y + r.h / 2;
          ctx.save();
          ctx.translate(cx, cy);
          ctx.rotate((rot * Math.PI) / 180);
          ctx.translate(-cx, -cy);
          ctx.setLineDash([4 / zoom, 3 / zoom]);
          ctx.strokeStyle = 'rgba(27,46,29,0.9)';
          ctx.lineWidth = 1.2 / zoom;
          ctx.strokeRect(r.x, r.y, r.w, r.h);
          ctx.setLineDash([]);
          const hs = handleSizeWorld(zoom);
          const pts: Array<[number, number]> = [
            [r.x, r.y],
            [cx, r.y],
            [r.x + r.w, r.y],
            [r.x + r.w, cy],
            [r.x + r.w, r.y + r.h],
            [cx, r.y + r.h],
            [r.x, r.y + r.h],
            [r.x, cy],
          ];
          for (const [px, py] of pts) {
            ctx.fillStyle = '#FFFFFF';
            ctx.strokeStyle = '#1B2E1D';
            ctx.lineWidth = 0.8 / zoom;
            ctx.fillRect(px - hs / 2, py - hs / 2, hs, hs);
            ctx.strokeRect(px - hs / 2, py - hs / 2, hs, hs);
          }
          const sel = selectionRef.current;
          if (sel && canRotate(sel.layer)) {
            const gap = rotateHandleGap(zoom);
            ctx.beginPath();
            ctx.moveTo(cx, r.y);
            ctx.lineTo(cx, r.y - gap);
            ctx.strokeStyle = '#1B2E1D';
            ctx.lineWidth = 1 / zoom;
            ctx.stroke();
            ctx.beginPath();
            ctx.arc(cx, r.y - gap, hs * 0.65, 0, Math.PI * 2);
            ctx.fillStyle = '#FFFFFF';
            ctx.fill();
            ctx.strokeStyle = '#1B2E1D';
            ctx.lineWidth = 0.9 / zoom;
            ctx.stroke();
          }
          ctx.restore();
        }
      }

      // ── Screen-space labels (constant size regardless of zoom) ──
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const toScreen = (wx: number, wy: number) => ({
        x: (wx - cam.x) * cam.zoom + w / 2,
        y: (wy - cam.y) * cam.zoom + h / 2,
      });
      // Station name tags sit under the stand point; hidden when zoomed far
      // out unless a manager is arranging (then they are the handles).
      if (cam.zoom >= 0.95 || arrangeRef.current || editRef.current) {
        ctx.font = '500 10px Geist, system-ui, sans-serif';
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'left';
        for (const t of stationTags) {
          const at = toScreen(t.x, t.y + 9);
          const text = t.occupied || t.placed ? t.name : `${t.name} · unplaced`;
          const tw = ctx.measureText(text).width;
          const pw = tw + 12;
          const ph = 16;
          const px = at.x - pw / 2;
          const py = at.y;
          roundRectPath(ctx, px, py, pw, ph, 8);
          ctx.fillStyle = t.occupied ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.72)';
          ctx.fill();
          ctx.strokeStyle = t.placed ? 'rgba(27,46,29,0.18)' : 'rgba(27,46,29,0.35)';
          ctx.lineWidth = 1;
          if (!t.placed) ctx.setLineDash([3, 2]);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillStyle = t.occupied ? '#1B2E1D' : 'rgba(27,46,29,0.6)';
          ctx.fillText(text, px + 6, py + ph / 2 + 0.5);
        }
      }

      const namesHidden = hideNamesRef.current;
      ctx.font = '500 11px Geist, system-ui, sans-serif';
      ctx.textBaseline = 'middle';
      for (const l of labels) {
        const head = toScreen(l.x, l.y - characterHeight() - 2);
        const nameText = namesHidden ? (l.isMe ? 'You' : '') : l.isMe ? `${l.name} (you)` : l.name;
        // With names hidden a bench pill shrinks to its work line and a
        // roaming character gets no pill at all (just the emote bubble).
        const text = nameText || l.sub || '';
        const sub = nameText ? l.sub : undefined;
        if (!text && !l.emote) continue;
        ctx.globalAlpha = l.alpha;
        let py = head.y - 2;
        if (text) {
          ctx.font = '500 11px Geist, system-ui, sans-serif';
          const tw = ctx.measureText(text).width;
          ctx.font = '400 10px Geist, system-ui, sans-serif';
          const sw = sub ? ctx.measureText(sub).width : 0;
          ctx.font = '500 11px Geist, system-ui, sans-serif';
          const padX = 7;
          const dot = 6;
          const pillW = Math.max(tw + dot + 4, sw) + padX * 2;
          const pillH = sub ? 31 : 18;
          const px = head.x - pillW / 2;
          py = head.y - pillH - 2;
          roundRectPath(ctx, px, py, pillW, pillH, 9);
          ctx.fillStyle = l.isMe
            ? 'rgba(197,221,133,0.96)'
            : l.tone === 'working'
              ? 'rgba(51,92,44,0.92)'
              : l.tone === 'break'
                ? 'rgba(14,65,94,0.9)'
                : 'rgba(27,46,29,0.84)';
          ctx.fill();
          const rowY = sub ? py + 10 : py + pillH / 2;
          ctx.fillStyle = STATUS_COLORS[l.status];
          ctx.beginPath();
          ctx.arc(px + padX + dot / 2, rowY, dot / 2, 0, Math.PI * 2);
          ctx.fill();
          if (l.status === 'online') {
            ctx.strokeStyle = 'rgba(255,255,255,0.7)';
            ctx.lineWidth = 1;
            ctx.stroke();
          }
          ctx.fillStyle = l.isMe ? '#1B2E1D' : '#FFFFFF';
          ctx.textAlign = 'left';
          ctx.fillText(text, px + padX + dot + 4, rowY + 0.5);
          if (sub) {
            ctx.font = '400 10px Geist, system-ui, sans-serif';
            ctx.fillStyle = l.isMe ? 'rgba(27,46,29,0.8)' : 'rgba(255,255,255,0.85)';
            ctx.fillText(sub, px + padX, py + 22.5);
            ctx.font = '500 11px Geist, system-ui, sans-serif';
          }
        }

        if (l.emote) {
          ctx.font = '18px system-ui, "Apple Color Emoji", "Segoe UI Emoji", sans-serif';
          const ew = ctx.measureText(l.emote).width + 14;
          const ex = head.x - ew / 2;
          const ey = py - 30;
          roundRectPath(ctx, ex, ey, ew, 26, 10);
          ctx.fillStyle = 'rgba(255,255,255,0.96)';
          ctx.fill();
          ctx.strokeStyle = 'rgba(27,46,29,0.18)';
          ctx.lineWidth = 1;
          ctx.stroke();
          // Little tail
          ctx.beginPath();
          ctx.moveTo(head.x - 4, ey + 26);
          ctx.lineTo(head.x, ey + 31);
          ctx.lineTo(head.x + 4, ey + 26);
          ctx.closePath();
          ctx.fillStyle = 'rgba(255,255,255,0.96)';
          ctx.fill();
          ctx.textAlign = 'center';
          ctx.fillStyle = '#1B2E1D';
          ctx.fillText(l.emote, head.x, ey + 14);
          ctx.font = '500 11px Geist, system-ui, sans-serif';
        }
        ctx.globalAlpha = 1;
      }

      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      onPingPongRef.current?.(null);
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointercancel', onPointerUp);
      canvas.removeEventListener('pointerleave', onPointerLeave);
      canvas.removeEventListener('wheel', onWheel);
    };
  }, [store]);

  return (
    <div ref={wrapRef} className={cn('relative h-full w-full overflow-hidden', className)}>
      <canvas
        ref={canvasRef}
        tabIndex={0}
        aria-label="Digital Company map"
        className="block h-full w-full cursor-crosshair touch-none select-none outline-none"
      />
    </div>
  );
});
